import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Inbox, Check, X, GitBranch, ExternalLink, RefreshCw, Filter, Code } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const STATUS_CFG = {
  pending:  { label: "En attente", color: "bg-yellow-500/20 text-yellow-400" },
  approved: { label: "Approuve",   color: "bg-green-500/20 text-green-400" },
  rejected: { label: "Rejete",     color: "bg-red-500/20 text-red-400" },
};

export default function ProjectInboxPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState({});
  const [rejectReason, setRejectReason] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const [itemsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/api/admin/project-inbox`, { headers, params }),
        axios.get(`${API}/api/clients`, { headers }),
      ]);
      setItems(itemsRes.data);
      setClients(clientsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (id) => {
    const clientId = selectedClient[id];
    if (!clientId) { toast.error("Selectionnez un client"); return; }
    try {
      await axios.post(`${API}/api/admin/project-inbox/${id}/approve`, { client_id: clientId }, { headers });
      toast.success("Projet approuve et cree");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleReject = async (id) => {
    try {
      await axios.post(`${API}/api/admin/project-inbox/${id}/reject`, { reason: rejectReason[id] || "" }, { headers });
      toast.success("Projet rejete");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const pendingCount = items.filter(i => i.status === "pending").length;

  return (
    <div className="space-y-6" data-testid="project-inbox">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Inbox className="h-6 w-6 text-[#0099CC]" /> Inbox Projets
            {pendingCount > 0 && (
              <span className="bg-[#0099CC]/20 text-[#0099CC] text-sm px-2.5 py-0.5 rounded-full font-semibold">{pendingCount}</span>
            )}
          </h1>
          <p className="text-[#64748b] text-sm mt-1">Projets importes via GitHub Webhook</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="refresh-inbox-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      {/* Webhook Info */}
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-lg p-4">
        <p className="text-[#94A3B8] text-sm">
          <GitBranch className="h-4 w-4 inline mr-1 text-[#0099CC]" />
          Configurez le webhook GitHub vers : <code className="bg-[#0B0F19] px-2 py-0.5 rounded text-[#0099CC] text-xs">{API}/api/webhooks/github</code>
        </p>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3" data-testid="inbox-filters">
        <Filter className="h-4 w-4 text-[#64748b]" />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-[#151B2B] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm"
          data-testid="inbox-filter-status"
        >
          <option value="">Tous</option>
          <option value="pending">En attente</option>
          <option value="approved">Approuves</option>
          <option value="rejected">Rejetes</option>
        </select>
      </div>

      {/* Items */}
      {items.length === 0 && !loading && (
        <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-12 text-center" data-testid="inbox-empty">
          <Inbox className="h-12 w-12 text-[#2A3441] mx-auto mb-3" />
          <p className="text-[#64748b]">Aucun projet en attente</p>
          <p className="text-[#64748b] text-sm mt-1">Les nouveaux repos GitHub apparaitront ici automatiquement.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map(item => {
          const cfg = STATUS_CFG[item.status] || STATUS_CFG.pending;
          return (
            <div key={item.id} className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-5" data-testid={`inbox-item-${item.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Left: Repo Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <Code className="h-5 w-5 text-[#0099CC] flex-shrink-0" />
                    <h3 className="text-white font-semibold text-lg truncate">{item.repo_name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                  </div>
                  <p className="text-[#64748b] text-sm mb-1">{item.repo_full_name}</p>
                  {item.description && <p className="text-[#94A3B8] text-sm mb-2">{item.description}</p>}
                  <div className="flex flex-wrap gap-3 text-xs text-[#64748b]">
                    {item.language && <span className="bg-[#0B0F19] px-2 py-1 rounded">{item.language}</span>}
                    <span>Branche: {item.default_branch}</span>
                    {item.sender && <span>Par: {item.sender}</span>}
                    <span>{new Date(item.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                  <a href={item.repo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0099CC] text-xs mt-2 hover:underline">
                    <ExternalLink className="h-3 w-3" /> Voir sur GitHub
                  </a>
                </div>

                {/* Right: Actions (only for pending) */}
                {item.status === "pending" && (
                  <div className="flex flex-col gap-2 lg:w-72 flex-shrink-0">
                    <select
                      value={selectedClient[item.id] || ""}
                      onChange={e => setSelectedClient(s => ({ ...s, [item.id]: e.target.value }))}
                      className="bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm w-full"
                      data-testid={`inbox-client-select-${item.id}`}
                    >
                      <option value="">Associer a un client...</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                    </select>
                    <div className="flex gap-2">
                      <Button onClick={() => handleApprove(item.id)} size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid={`inbox-approve-${item.id}`}>
                        <Check className="h-4 w-4 mr-1" /> Approuver
                      </Button>
                      <Button onClick={() => handleReject(item.id)} size="sm" variant="outline" className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`inbox-reject-${item.id}`}>
                        <X className="h-4 w-4 mr-1" /> Rejeter
                      </Button>
                    </div>
                    <input
                      type="text"
                      placeholder="Raison du rejet (optionnel)"
                      value={rejectReason[item.id] || ""}
                      onChange={e => setRejectReason(s => ({ ...s, [item.id]: e.target.value }))}
                      className="bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-1.5 text-xs w-full placeholder-[#64748b]"
                      data-testid={`inbox-reject-reason-${item.id}`}
                    />
                  </div>
                )}

                {/* Show client association for approved */}
                {item.status === "approved" && item.client_id && (
                  <div className="text-right">
                    <span className="text-green-400 text-xs">Projet cree</span>
                  </div>
                )}

                {/* Show rejection reason */}
                {item.status === "rejected" && item.rejected_reason && (
                  <div className="text-right">
                    <span className="text-red-400 text-xs">Raison: {item.rejected_reason}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
